// src/utils/exportExpenseHelpers.js
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Map vendors by id
const mapVendors = (vendors) => {
  const map = {};
  vendors.forEach((v) => {
    map[v.id] = v.name || v.fullname || v.business_name || "Unknown Vendor";
  });
  return map;
};

// ------------------ CSV EXPORT ------------------
export const exportCSV = (rows, filename = "expenses.csv", vendors = []) => {
  if (!rows || rows.length === 0) return;

  const vendorMap = mapVendors(vendors);
  const cleaned = rows.map((r) => ({
    Description: r.description,
    Amount: r.amount,
    Category: r.category,
    Supplier: r.supplier || "-",
    Vendor: vendorMap[r.vendor_id] || r.vendor_name || "-",
    Date: new Date(r.expense_date).toLocaleString(),
  }));

  const headers = Object.keys(cleaned[0]).join(",");
  const body = cleaned
    .map((row) =>
      Object.values(row)
        .map((val) => `"${val !== null ? val : ""}"`)
        .join(",")
    )
    .join("\n");

  const csv = `${headers}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

// ------------------ EXCEL EXPORT ------------------
export const exportExcel = (rows, filename = "expenses.xlsx", vendors = []) => {
  const vendorMap = mapVendors(vendors);
  const cleaned = rows.map((r) => ({
    Description: r.description,
    Amount: r.amount,
    Category: r.category,
    Supplier: r.supplier || "-",
    Vendor: vendorMap[r.vendor_id] || r.vendor_name || "-",
    Date: new Date(r.expense_date).toLocaleString(),
  }));

  const ws = XLSX.utils.json_to_sheet(cleaned);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Expenses");
  XLSX.writeFile(wb, filename);
};

// ------------------ PDF EXPORT ------------------
export const exportPDF = (rows, filename = "expenses.pdf", vendors = [], logo = "/logo.png") => {
  if (!rows || rows.length === 0) return;

  const vendorMap = mapVendors(vendors);
  const cleaned = rows.map((r) => ({
    Description: r.description,
    Amount: `₦${Number(r.amount).toLocaleString()}`,
    Category: r.category,
    Supplier: r.supplier || "-",
    Vendor: vendorMap[r.vendor_id] || r.vendor_name || "-",
    Date: new Date(r.expense_date).toLocaleString(),
  }));

  const headers = Object.keys(cleaned[0]);
  const data = cleaned.map((r) => Object.values(r));

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  try {
    doc.addImage(logo, "PNG", 40, 20, 80, 40);
  } catch {}

  doc.setFontSize(20);
  doc.text("Expense Report", 200, 60);

  // Watermark (removed as per user request to replace Deesoftwork branding)
  /*
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(60);
  doc.text("DEESOFTWORK", doc.internal.pageSize.width / 2 - 150, 250, {
    angle: 45,
    opacity: 0.1,
  });
  */
  doc.setTextColor(0, 0, 0);

  const paymentColIndex = headers.indexOf("Payment"); // optional if you have payment breakdown

  autoTable(doc, {
    head: [headers],
    body: data,
    startY: 100,
    styles: { fontSize: 10, cellPadding: 6, overflow: "linebreak" },
    headStyles: { fillColor: [40, 40, 40], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { top: 100, bottom: 50 },
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      const size = doc.internal.pageSize;
      doc.setFontSize(10);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        data.settings.margin.left,
        size.height - 20
      );

      // Footer
      doc.setFontSize(10);
      doc.text("Generated by Business Management System", data.settings.margin.left, size.height - 5);
    },
  });

  doc.save(filename);
};
